import { useState } from 'react';
import {
  dayFromLocalDate,
  formatDayLong,
  formatMonthYear,
  localDateFromDay,
  monthGridDayRange,
  shiftMonth,
  todayDayIndex,
  weekdayOfDay,
} from '@betreuung/shared';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';

export interface DateFieldProps {
  dayIndex: number;
  onChange: (dayIndex: number) => void;
}

/**
 * Date picker for the modal's from/to rows.
 *
 * Hand-built rather than pulled from a date library: the project rules out an
 * external calendar dependency, and the month grid logic already exists in
 * shared/slots.ts.
 */
export function DateField({ dayIndex, onChange }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = localDateFromDay(dayIndex);
  const [cursor, setCursor] = useState({ year: selected.year, month: selected.month });

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reopening should always land on the month of the current value.
        if (next) setCursor({ year: selected.year, month: selected.month });
      }}
    >
      <PopoverTrigger
        className={cn(
          'flex flex-1 items-center justify-between gap-2 rounded-control border border-line bg-card',
          'px-3 py-2.5 text-body font-medium text-fg',
        )}
      >
        {formatDayLong(dayIndex)}
        <ChevronDown className="size-2.5 shrink-0 text-fg-hint" />
      </PopoverTrigger>
      <PopoverContent className="w-[280px]">
        <MiniCalendar
          cursor={cursor}
          onCursorChange={setCursor}
          selectedDay={dayIndex}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function MiniCalendar({
  cursor,
  onCursorChange,
  selectedDay,
  onSelect,
}: {
  cursor: { year: number; month: number };
  onCursorChange: (cursor: { year: number; month: number }) => void;
  selectedDay: number;
  onSelect: (dayIndex: number) => void;
}) {
  const { firstDay, lastDay } = monthGridDayRange(cursor.year, cursor.month);
  const days: number[] = [];
  for (let day = firstDay; day <= lastDay; day += 1) days.push(day);
  const today = todayDayIndex();

  return (
    <div>
      <div className="flex items-center justify-between pb-2">
        <button
          type="button"
          aria-label={de.month.previous}
          onClick={() => onCursorChange(shiftMonth(cursor.year, cursor.month, -1))}
          className="hit-44 flex size-7 items-center justify-center rounded-cell border border-line text-fg-tertiary"
        >
          <ChevronLeft className="size-3.5" />
        </button>
        <span className="text-body font-semibold text-fg">
          {formatMonthYear(cursor.year, cursor.month)}
        </span>
        <button
          type="button"
          aria-label={de.month.next}
          onClick={() => onCursorChange(shiftMonth(cursor.year, cursor.month, 1))}
          className="hit-44 flex size-7 items-center justify-center rounded-cell border border-line text-fg-tertiary"
        >
          <ChevronRight className="size-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 pb-1">
        {de.month.weekdays.map((label) => (
          <span key={label} className="text-center font-mono text-meta text-fg-hint">
            {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const date = localDateFromDay(day);
          const inMonth = date.month === cursor.month && date.year === cursor.year;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelect(day)}
              className={cn(
                'flex h-8 items-center justify-center rounded-cell text-label',
                inMonth ? 'text-fg' : 'text-fg-hint',
                day === selectedDay
                  ? 'bg-accent font-semibold text-accent-fg'
                  : 'hover:bg-muted',
                day === today && day !== selectedDay && 'ring-today',
              )}
            >
              {date.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Convenience for the modal: the day index a slot belongs to. */
export function dayOfDate(year: number, month: number, day: number): number {
  return dayFromLocalDate(year, month, day);
}

/** Weekday helper re-exported so the modal need not import slots directly. */
export { weekdayOfDay };
